import React, {
    createContext,
    useEffect,
    useState,
    useContext,
    useRef,
  } from "react";
  
  //CONSTANT
  import { ACTION_EVENT } from "../constant/event";
  import {
    BUNDLE_APP_NAME,
    GET_APP_DOMAIN_LIST,
    GET_APP_NAME,
    IS_BUNDLE_APP,
    GET_APP_NAME_FOR_COLLECTION,
    APP_BUNDLES,
    IS_VISIBLE_COMMUNITY,
  } from "../../app_constant";
  import { STRING } from "../constant";
  
  //API
  import { UPDATE_SUBSCRIPTION, UPDATE_COMMUNITY_DATA } from "../api/community";
  import {
    GET_STRIPE_DETAILS,
    GET_USER_STRIPE_ONE_TIME_PAYMENT,
    GET_REVENUECAT_EVENTS,
    GET_REVENUECAT_METADATA,
    GET_REVENUECAT_SUBSCRIPTION_PLAN,
    WRITE_CANCELLATION_SURVEY,
    UPDATE_NOTIFICATION_SETTING,
    UPDATE_PROFILE_DETAILS,
    UPDATE_USER_FONTS_OPTIONS,
    UPDATE_USER_PROFILE,
    UPDATE_USER_TEST_OPTIONS,
    UPDATE_BUNDLE_PROFILE_DETAILS,
    UPDATE_BUNDLE_USER_PROFILE,
  } from "../api/firebase_user";
  import { GET_SCORE_QUESTIONS, SAVE_QUESTION, MIGRATE_QUESTION_SCORES, SAVE_PROGRESS_DASHBOARD, SAVE_SCORE_PROGRESS, SAVE_QUESTION_BOOKMARK, SAVE_QUESTION_PROGRESS } from "../api/questions";
  import {
    GET_ACCESS_CODE_DETAILS,
    UPDATE_ACCESS_CODE_REDEEM,
  } from "../api/access_code";
  import {
    SAVE_FLASHCARD_BOOKMARK,
    SAVE_FLASHCARD_PROGRESS,
  } from "../api/flashcard";
  import {
    SAVE_ARTICLE_BOOKMARK,
    SAVE_ARTICLE_PROGRESS,
    GET_ARTICLE_BOOKMARKS,
    MARK_ARTICLE_AS_READ,
    GET_ARTICLE_READ_STATUS,
    GET_ARTICLE_READ_SUMMARY,
    GET_READ_ARTICLES_BY_TOPIC,
    SAVE_ARTICLE_REACTION,
    GET_ARTICLE_REACTION_SUMMARY,
  } from "../api/articles";
  import { DELETE_TEST_HISTORY, SAVE_TEST_HISTORY } from "../api/practice";
  import {
    UPDATE_USER_QOTD,
    GET_USER_QOTD_SUMMARY,
    GET_USER_QOTD_RESPONSE,
    GET_USER_QOTD_RESPONSES,
  } from "../api/qotd";
  
  //CONTENT
  import { GET_ARTICLES, GET_FLASHCARDS, GET_LOCAL_QUESTIONS, GET_ACRONYMS, GET_GLOSSARY } from "../api/content";
  
  //CONTEXT
  import { BundleContext } from "./BundleProvider";
  import { isExamEntitledFor } from "../utils/subscriptionScope";
  
  //PACKAGES
  import { auth, firestore } from "../api/config";
  import { usePostHog } from 'posthog-js/react';
import { isGhostMode, getGhostUid, getEffectiveUid } from "../api/ghostUid";
import moment from "moment";
import _ from "lodash";
import { getTodayLocal } from "../utils/dates";
import {
  deriveRevenueCatState,
  deriveStripeState,
  deriveStripeOneTimeState,
  pickBestState,
  isActiveAt,
} from "../utils/subscriptionState";

//CORE BUSINESS LOGIC (Shared with mobile)
/* import {
  calculateReadinessScores,
  normalizeExpirationDate,
  isSubscriptionActive,
  isSubscriptionMetadataComplete,
  getPlanFromInterval,
  createEmptyProfile as createEmptyProfileCore,
  createEmptyArticlesData as createEmptyArticlesDataCore,
  createEmptyQOTDData as createEmptyQOTDDataCore,
  createEmptyBundleData,
  createEmptyCommunityData
} from "../../lzapps_core_code/business/authProvider"; */

// Shared, bundle-anchored readiness calculator (single source of truth with mobile).
import { calculateReadinessScores as calculateReadinessScoresCore, planReadinessRecalculation } from "../../lzapps_core_code/business/authProvider/readinessScoreCalculator";

const collection = async () => {
  const APP_NAME = await GET_APP_NAME_FOR_COLLECTION();
  return firestore()
    .collection("apps")
    .doc(APP_NAME)
    .collection("users")
    .doc(getEffectiveUid());
};

export const AuthContext = createContext();
  
  export const AuthProvider = (props) => {
    const { selectedCertificate } = useContext(BundleContext);
    const posthog = usePostHog();
  
    // Get initial profile data from localStorage
    const getInitialProfileData = () => {
      const userdata = localStorage.getItem("userData");
      const data = JSON.parse(userdata) || null;
      return data || {
        auth_email: "",
        auth_first_name: "",
        auth_last_name: "",
        auth_login_provider: "",
        last_signin_time: "",
        flashcard_bookmarks: [],
        flashcards_progress: [],
        practice_test_history: [],
        question_bookmarks: [],
        questions_progress: [],
        articles_progress: [],
        quiz_of_the_day_reminder: true,
        signup_date: "",
        study_plan_reminder: true,
        profile_pic: "",
        readiness_scores: null,
        score_progress: {},
      };
    };

    // Get initial articles data from localStorage
    const getInitialArticlesData = () => {
      const articlesData = localStorage.getItem("articlesData");
      const data = JSON.parse(articlesData) || null;
      return data || {
        bookmarks: [],
        readSummary: {
          total_articles_read: 0,
          total_read_count: 0,
          last_read_at: null,
          articles_by_topic: {}
        },
        reactionSummary: {
          total_articles_liked: 0,
          total_articles_disliked: 0,
          last_reaction_at: null,
          reactions_by_topic: {}
        }
      };
    };

    // Get initial QOTD data from localStorage
    const getInitialQOTDData = () => {
      const qotdData = localStorage.getItem("qotdData");
      const data = JSON.parse(qotdData) || null;
      return data || {
        summary: {
          current_streak: 0,
          best_streak: 0,
          total_answered: 0,
          total_correct: 0,
          last_qotd_date: null
        },
        responses: {}
      };
    };

    // State declarations
    const [profile, setProfileState] = useState(getInitialProfileData());
    // Always-current mirror of `profile` so progress writes never rebuild from a
    // stale closure. Effect lags one commit; the per-set monotonic guard in
    // addQuestionProgress makes that lag harmless for write ordering.
    const profileRef = useRef(profile);
    useEffect(() => { profileRef.current = profile; }, [profile]);
    // Serialize study-progress writes (one at a time) with a per-set monotonic
    // sequence so an older last_seen_question_id can never clobber a newer one.
    const _progressWriteChain = useRef(Promise.resolve());
    const _latestSeenBySet = useRef(new Map()); // setId -> seq
    const [articles, setArticlesState] = useState(getInitialArticlesData());
    const [qotd, setQOTDState] = useState(getInitialQOTDData());
    const [bundleData, setBundleDataState] = useState({
      auth_email: "",
      auth_first_name: "",
      auth_last_name: "",
      auth_login_provider: "",
      last_signin_time: "",
    });
    const [community, setCommunityState] = useState({
      questions_version: 1,
      total_time_per_question: 90,
      is_forcefully_new_version: false,
      android_app_verions: null,
      android_build_number: null,
      ios_app_version: null,
      ios_build_number: null,
    });
    const [questionScore, setQuestionScoreState] = useState({
      all_questions_scores: [],
    });
  
    const [questionsList, setQuestionsList] = useState([]);
    const [articlesList, setArticlesList] = useState([]);
    const [flashCardsList, setFlashCardsList] = useState([]);
    const [acronymsList, setAcronymsList] = useState([]);
    const [glossaryList, setGlossaryList] = useState([]);
    const [isSubscribe, setSubscription] = useState(false);
    const [isVisibleCommunity, setVisibleCommunity] = useState(IS_VISIBLE_COMMUNITY);
    
    // Authentication loading state
    const [isAuthLoading, setIsAuthLoading] = useState(true);

    // Single source of truth for premium access (`isSubscribe`). Derived
    // reactively from the PERSISTED subscription doc — whichever of profile /
    // bundleData holds it (mirrors the Profile screen's getPlanExpiry) — so the
    // header, paywalls, and Profile never diverge. We do NOT set isSubscribe
    // from the live derivation candidate: updateSubscription's guards may
    // preserve a longer mobile-written (Apple/RC) expiration than web can
    // re-derive, and the persisted doc is the authoritative post-guard state.
    // The grant/deny rules (billing_retry grace, refunded/expired/grace revoke)
    // live in @learnzapp/core's isPremiumSubscription so web and mobile agree.
    // `isExamEntitledFor` resolves the right entitlement object per brand: white-label
    // reads the PER-EXAM `subscriptions[selectedCertificate]` map (old wl-webapp model — so a
    // partner can grant CISSP-only or CISSP+CCSP, and old users with only that map still work);
    // non-WL brands read the singular `subscription`. Re-runs on exam switch (per-exam lock).
    useEffect(() => {
      setSubscription(
        isExamEntitledFor(profile, selectedCertificate) ||
        isExamEntitledFor(bundleData, selectedCertificate)
      );
    }, [
      profile?.subscription,
      bundleData?.subscription,
      profile?.subscriptions,
      bundleData?.subscriptions,
      selectedCertificate,
    ]);

    // Helper function to update localStorage when profile changes
    const updateLocalStorage = (newProfile) => {
      localStorage.setItem("userData", JSON.stringify(newProfile));
    };

    // Helper function to update articles localStorage
    const updateArticlesLocalStorage = (newArticles) => {
      localStorage.setItem("articlesData", JSON.stringify(newArticles));
    };

    // Helper function to update QOTD localStorage
    const updateQOTDLocalStorage = (newQOTD) => {
      localStorage.setItem("qotdData", JSON.stringify(newQOTD));
    };

    // Helper function to clear user-specific localStorage data
    const clearUserLocalStorage = () => {
      localStorage.removeItem("userData");
      localStorage.removeItem("articlesData");
      localStorage.removeItem("qotdData");
    };
  
    // Delegates to the shared, bundle-anchored readiness calculator so web and mobile agree and the
    // dashboard matches the custom-test pool. (Replaces an inline duplicate that counted answered
    // *records* with strict topic filtering and divided by zero on empty domains.)
    const calculateReadinessScores = (scores, questionsList, appDomains) =>
      calculateReadinessScoresCore(scores, questionsList, appDomains);
  
    // Get score questions on mount
    async function getScoreQuestions() {
      const result = await GET_SCORE_QUESTIONS();
      setAllQuestionData(result?.data ?? []);
      setQuestionScoreState({
        all_questions_scores: result?.data ?? [],
      });
      return { data: result?.data ?? [], exist: result.isExist };
    }
  
    useEffect(() => {
      getScoreQuestions();
    }, []);
  
    useEffect(() => {
      const QUESTIONS_LIST = GET_LOCAL_QUESTIONS(selectedCertificate);
      setQuestionsList(QUESTIONS_LIST);

      // Article bodies (~11 MB) load lazily on demand via loadArticlesList() — not
      // at startup — to keep them out of the initial bundle. Reset on cert change so
      // a previously-loaded cert's articles don't leak into the new selection.
      setArticlesList([]);

      const FLASH_CARDS_LIST = GET_FLASHCARDS(selectedCertificate);
      setFlashCardsList(FLASH_CARDS_LIST);

      const ACRONYMS_LIST = GET_ACRONYMS(selectedCertificate);
      setAcronymsList(ACRONYMS_LIST);

      const GLOSSARY_LIST = GET_GLOSSARY(selectedCertificate);
      setGlossaryList(GLOSSARY_LIST);
    }, [selectedCertificate]);

    // Lazily fetch the article chunk for the selected cert and cache it in context so
    // the Articles screen, its article reader, and RelatedStudyArticles share one load.
    // Keyed on the cert (not just list length) so a cached list from a previous cert
    // never short-circuits, and a slow chunk fetch that resolves after the user has
    // switched certs is dropped instead of clobbering the new selection.
    const loadedCertRef = useRef(null);
    const loadArticlesList = async (cert = selectedCertificate) => {
      if (loadedCertRef.current === cert && articlesList.length) return articlesList;
      const list = await GET_ARTICLES(cert);
      if (cert !== selectedCertificate) return list; // stale: cert changed mid-fetch — don't commit
      loadedCertRef.current = cert;
      setArticlesList(list);
      return list;
    };

    useEffect(() => {
      const unsubscribeAuth = auth().onAuthStateChanged((user) => {
        // Set loading to false once we get the auth state
        setIsAuthLoading(false);
        // console.log('[AuthProvider] onAuthStateChanged:', user ? 'User logged in' : 'User logged out');

        if (user) {
          // Identify only when the distinct_id changes — passing traits to identify
          // emits a $set event each call, so guarding here avoids one $set per
          // onAuthStateChanged firing (page load, token refresh, etc.).
          if (posthog && posthog.get_distinct_id() !== user.uid) {
            posthog.identify(user.uid, {
              email: user.email,
              name: user.displayName,
              app_name: BUNDLE_APP_NAME,
            });
          }

          const effectiveUid = isGhostMode() ? getGhostUid() : user.uid;
          const bundle = APP_BUNDLES.find(b => b.key === selectedCertificate);
          const APP_NAME = bundle ? bundle.bundle_firebase_collection_name : GET_APP_NAME(selectedCertificate);

          const unsubscribeProfile = firestore()
            .collection("apps")
            .doc(APP_NAME)
            .collection("users")
            .doc(effectiveUid)
            .onSnapshot((snapshot) => {
              if (snapshot.exists) {
                const data = snapshot.data();
                setProfile(data);

                // Save country_code if not already in profile (skip in ghost mode)
                if (data.article_bookmarks !== undefined) {
                  setArticlesState((prev) => {
                    const updated = {
                      ...prev,
                      bookmarks: data.article_bookmarks || [],
                    };
                    updateArticlesLocalStorage(updated);
                    return updated;
                  });
                }
                // isSubscribe is derived reactively from profile/bundleData
                // (see the effect near the state declarations) — not set here —
                // so the header, paywalls, and Profile read the same persisted
                // subscription and can't diverge.
              }
            });

          const unsubscribeQuestions = firestore()
            .collection("apps")
            .doc(APP_NAME)
            .collection("users")
            .doc(effectiveUid)
            .collection("questions")
            .onSnapshot((querySnapshot) => {
              const questions = [];
              querySnapshot.forEach((doc) => {
                questions.push(doc.data());
              });
              setQuestions(questions);
            });

          // Articles real-time listeners
          const unsubscribeArticles = firestore()
            .collection("apps")
            .doc(APP_NAME)
            .collection("users")
            .doc(effectiveUid)
            .collection("articles")
            .onSnapshot((querySnapshot) => {
              // Preserve existing state to avoid resetting progress that hasn't been saved to Firestore yet
              setArticlesState((prevArticles) => {
                const newArticles = {
                  bookmarks: prevArticles?.bookmarks || [],
                  readSummary: prevArticles?.readSummary || {
                    total_articles_read: 0,
                    total_read_count: 0,
                    last_read_at: null,
                    articles_by_topic: {}
                  },
                  reactionSummary: prevArticles?.reactionSummary || {
                    total_articles_liked: 0,
                    total_articles_disliked: 0,
                    last_reaction_at: null,
                    reactions_by_topic: {}
                  }
                };

                querySnapshot.forEach((doc) => {
                  const data = doc.data();
                  switch (doc.id) {
                    case "article_read_summary":
                      newArticles.readSummary = {
                        total_articles_read: data.total_articles_read || 0,
                        total_read_count: data.total_read_count || 0,
                        last_read_at: data.last_read_at || null,
                        articles_by_topic: data.articles_by_topic || {}
                      };
                      break;
                    case "article_reaction_summary":
                      newArticles.reactionSummary = {
                        total_articles_liked: data.total_articles_liked || 0,
                        total_articles_disliked: data.total_articles_disliked || 0,
                        last_reaction_at: data.last_reaction_at || null,
                        reactions_by_topic: data.reactions_by_topic || {}
                      };
                      break;
                    default:
                      // Handle individual article read status documents
                      if (data.article_id) {
                        // This is an individual article read status
                        // We could store this in readSummary.articles_by_topic if needed
                      }
                      break;
                  }
                });

                updateArticlesLocalStorage(newArticles);
                return newArticles;
              });
            });

          // QOTD real-time listeners - Optimized to only load summary and today's response
          const today = getTodayLocal(); // Use local timezone date
          
          // Reset QOTD state when exam changes
          setQOTDState(getInitialQOTDData());
          
          // Add debug info to state
          setQOTDState(prev => ({
            ...prev,
            _debug: {
              today,
              appName: APP_NAME,
              timestamp: new Date().toISOString(),
              collectionPath: `apps/${APP_NAME}/users/${effectiveUid}/qotd`,
              listenerSetup: true
            }
          }));
          
          // Listen to summary document
          const unsubscribeQOTDSummary = firestore()
            .collection("apps")
            .doc(APP_NAME)
            .collection("users")
            .doc(effectiveUid)
            .collection("qotd")
            .doc("summary")
            .onSnapshot((doc) => {
              if (doc.exists) {
                const data = doc.data();
                setQOTDState(prev => ({
                  ...prev,
                  summary: {
                    current_streak: data.current_streak || 0,
                    best_streak: data.best_streak || 0,
                    total_answered: data.total_answered || 0,
                    total_correct: data.total_correct || 0,
                    last_qotd_date: data.last_qotd_date || null
                  },
                  _debug: {
                    ...prev._debug,
                    summaryExists: true,
                    summaryData: data
                  }
                }));
              } else {
                // Summary document doesn't exist for this exam, reset to initial state
                setQOTDState(prev => ({
                  ...prev,
                  summary: {
                    current_streak: 0,
                    best_streak: 0,
                    total_answered: 0,
                    total_correct: 0,
                    last_qotd_date: null
                  },
                  _debug: {
                    ...prev._debug,
                    summaryExists: false,
                    summaryData: null
                  }
                }));
              }
            });

          // Listen to today's response only (exclude summary document)
          const unsubscribeQOTDResponses = firestore()
            .collection("apps")
            .doc(APP_NAME)
            .collection("users")
            .doc(effectiveUid)
            .collection("qotd")
            .where('date', '>=', today) // Only load today and future responses
            .onSnapshot((querySnapshot) => {
              const newResponses = {};
              const debugData = [];
              
              querySnapshot.forEach((doc) => {
                const data = doc.data();
                debugData.push({ docId: doc.id, data });
                
                // Only process documents that have a date field (actual responses, not summary)
                if (data.date && data.date >= today && doc.id !== "summary") {
                  // Only load today's and future responses
                  newResponses[doc.id] = {
                    date: data.date,
                    isCorrect: data.isCorrect,
                    timeSpent: data.timeSpent || 0,
                    answered_at: data.answered_at,
                    ...data
                  };
                }
              });

              setQOTDState(prev => ({
                ...prev,
                responses: newResponses,
                _debug: {
                  ...prev._debug,
                  responsesQuerySize: querySnapshot.size,
                  responsesLoaded: Object.keys(newResponses).length,
                  allQueryData: debugData,
                  lastQueryTime: new Date().toISOString(),
                  queryFilter: `date >= ${today}`
                }
              }));
              updateQOTDLocalStorage({
                summary: qotd?.summary || {
                  current_streak: 0,
                  best_streak: 0,
                  total_answered: 0,
                  total_correct: 0,
                  last_qotd_date: null
                },
                responses: newResponses
              });
            });

          let unsubscribeBundle = null;
          if (IS_BUNDLE_APP) {
            unsubscribeBundle = firestore()
              .collection("apps")
              .doc(BUNDLE_APP_NAME)
              .collection("users")
              .doc(effectiveUid)
              .onSnapshot((snapshot) => {
                if (snapshot.exists) {
                  const data = snapshot.data();
                  setBundleProfile(data);
                  // isSubscribe is derived reactively from bundleData/profile
                  // (see the effect near the state declarations) — not set here.
                }
              });
          }

          // Raw-collection listeners drive derivation. The Firebase Stripe
          // Extension writes to customers/{uid}/subscriptions on every Stripe
          // webhook; the learnzapp_prod_revenueCatWebhook function appends to
          // revenuecat/{uid}/events on every RC event. Either change here re-
          // runs derivation, which writes the user-doc subscription field.
          // The initial onSnapshot delivery also serves as our lazy-fill for
          // pre-existing users on first load.
          // getSubscriptionDetails always reads the latest user-doc subscription
          // inside updateSubscription's transaction, so no closure state needed.
          const triggerDerivation = () => {
            if (isGhostMode()) return;
            getSubscriptionDetails();
          };

          const unsubscribeStripeSubs = firestore()
            .collection("customers")
            .doc(effectiveUid)
            .collection("subscriptions")
            .onSnapshot(() => triggerDerivation(), (err) => {
              console.error("Stripe subscriptions listener error:", err);
            });

          const unsubscribeRcEvents = firestore()
            .collection("revenuecat")
            .doc(effectiveUid)
            .collection("events")
            .onSnapshot(() => triggerDerivation(), (err) => {
              console.error("RevenueCat events listener error:", err);
            });

          return () => {
            unsubscribeProfile();
            unsubscribeQuestions();
            unsubscribeArticles();
            unsubscribeQOTDSummary();
            unsubscribeQOTDResponses();
            unsubscribeBundle && unsubscribeBundle();
            unsubscribeStripeSubs();
            unsubscribeRcEvents();
          };
        } else {
          // User signed out - clear all user data
          setProfileState(null);
          setBundleDataState(null);
          setQuestionScoreState({ all_questions_scores: [] });
          setArticlesList([]);
          setQOTDState(getInitialQOTDData());
          setSubscription(false);
          setVisibleCommunity(IS_VISIBLE_COMMUNITY);
          clearUserLocalStorage();
        }
      });

      return () => unsubscribeAuth();
    }, [selectedCertificate]);
  
    // Writes the derived subscription back to the user doc. Transactional read-
    // modify-write so concurrent tabs / listeners can't clobber each other:
    // only proceeds if the incoming derived_at is newer than what's persisted.
    // Merges with the existing subscription so unrelated fields (licenseCode,
    // assigned_email, period for bulk_purchase) survive a derivation pass that
    // only knows about RC/Stripe sources. No-op in ghost mode.
    const updateSubscription = async (newSubscription, derivationContext = {}) => {
      if (isGhostMode()) return;
      // Survey to write AFTER the transaction commits — used when Stripe Portal
      // already captured a cancellation reason and we want to record it
      // directly without prompting the user in-app.
      let stripePortalSurvey = null;
      try {
        const APP_NAME = IS_BUNDLE_APP
          ? BUNDLE_APP_NAME
          : await GET_APP_NAME_FOR_COLLECTION();
        const uid = auth().currentUser?.uid;
        if (!uid) return;
        const docRef = firestore()
          .collection("apps")
          .doc(APP_NAME)
          .collection("users")
          .doc(uid);
        await firestore().runTransaction(async (tx) => {
          const snap = await tx.get(docRef);
          const existing = snap.exists ? (snap.data()?.subscription || {}) : {};
          const existingSurvey = snap.exists ? (snap.data()?.survey || {}) : {};

          // Manual-override carve-outs (must run before idempotency check):
          //
          // 1) Explicit admin lock — if the doc already has manual_override:
          //    true, derivation must never touch it.
          if (existing.manual_override === true) return;

          // 2) Implicit never-shorten rule — if the existing expiration is in
          //    the future AND beyond anything derivation found, an admin has
          //    hand-edited subscription_expiration to grant or extend access.
          //    Preserve the doc as-is, but auto-set manual_override: true so
          //    future passes short-circuit on the explicit check above and
          //    analytics can identify manual grants cleanly. Dot-path update
          //    so other subscription fields aren't replaced.
          //    Exclusions: a refund or a non-payment state (billing_retry /
          //    grace / expired) legitimately backdates the expiration to the
          //    last-paid boundary while the stored one is still the optimistic
          //    future period_end — excluding these lets the shortening persist
          //    instead of freezing the user as active (the billing_retry
          //    entitlement leak). pickBestState only hands us a non-payment state
          //    when no longer-paid source exists, so this can't clobber a legit
          //    mobile/admin entitlement; explicit admin locks (manual_override
          //    === true, handled above) stay fully protected.
          //    (Plan changes can't shorten the period here: users only upgrade,
          //    never downgrade while active, so no proration moves period_end
          //    earlier. Natural expiry never reaches this branch — existingExp
          //    would already be in the past.)
          const NONPAYMENT_STATUSES = ['refunded', 'billing_retry', 'grace', 'expired'];
          const now = Date.now();
          const existingExp = Number(existing.subscription_expiration) || 0;
          const newExp = Number(newSubscription?.subscription_expiration) || 0;
          if (
            existingExp > now &&
            existingExp > newExp &&
            !NONPAYMENT_STATUSES.includes(newSubscription?.status)
          ) {
            if (!existing.manual_override) {
              tx.update(docRef, { "subscription.manual_override": true });
            }
            return;
          }

          // No-op guard — derivation runs on every app load, so skip the write
          // entirely when the derived state is identical to what's stored. We
          // compare the meaningful derived fields rather than a timestamp: a
          // Date.now() stamp always looks newer (so it wrote every load), while
          // a source-event timestamp doesn't move when Stripe's
          // cancel_at_period_end flips (period end unchanged) — that would drop
          // real cancels. Field comparison sidesteps both. Runs before the
          // cancel-transition check below, which only matters when state changed.
          const unchanged =
            existing.status === newSubscription.status &&
            existing.will_renew === newSubscription.will_renew &&
            Number(existing.subscription_expiration) === Number(newSubscription.subscription_expiration) &&
            existing.paymentMode === newSubscription.paymentMode &&
            (existing.plan ?? null) === (newSubscription.plan ?? null) &&
            (existing.derived_event_id ?? null) === (newSubscription.derived_event_id ?? null);
          if (unchanged) return;

          // Cancel-transition detection. Fires only when going from a known-
          // active state to cancelled/refunded. Skip when existing.status is
          // undefined (first derivation for a pre-existing user — could be a
          // months-old cancel we shouldn't survey) and when a survey is
          // already pending (dedup). Also skip if the user already answered the
          // in-app pre-cancel survey THIS cycle (pre_cancel_at is set; it's
          // cleared on resubscribe). We gate on existence, not a 10-minute
          // window — a slow Stripe Portal (>10 min) would otherwise set
          // cancel_reason_pending and re-ask "why did you leave?" for a reason
          // they already gave.
          const wasActive = existing.status === 'active';
          const isCancelledNow = newSubscription.status === 'cancelled' || newSubscription.status === 'refunded';
          const noSurveyPending = !existingSurvey.cancel_reason_pending;
          const preCancelAtMs = existingSurvey.pre_cancel_at?.toMillis?.()
            ?? existingSurvey.pre_cancel_at
            ?? 0;
          const preCancelAnswered = preCancelAtMs > 0;
          const isCancelTransition = wasActive && isCancelledNow && noSurveyPending && !preCancelAnswered;

          const writes = { subscription: { ...existing, ...newSubscription } };

          // Resubscribe reset — when the user returns to an active state, wipe
          // the prior cycle's post-cancel survey routing. Without this, a stale
          // helped_pass / pending flag from an earlier cancel would re-trigger
          // the success-story or why-left asks after a *later* cancel.
          const isActiveNow = newSubscription.status === 'active';
          const hadPostCancelState =
            existing.status &&
            existing.status !== 'active' &&
            (existingSurvey.cancel_reason_pending ||
              existingSurvey.helped_pass !== undefined ||
              existingSurvey.post_cancel_action ||
              existingSurvey.pre_cancel_at ||
              existingSurvey.cancel_prompt_dismissed);
          if (isActiveNow && hadPostCancelState) {
            writes['survey.cancel_reason_pending'] = false;
            writes['survey.cancel_prompt_dismissed'] = false;
            writes['survey.helped_pass'] = firestore.FieldValue.delete();
            writes['survey.post_cancel_action'] = firestore.FieldValue.delete();
            writes['survey.pre_cancel_at'] = firestore.FieldValue.delete();
            writes['survey.last_reason'] = firestore.FieldValue.delete();
          }

          if (isCancelTransition) {
            if (derivationContext.stripeCancellationReason) {
              // Stripe Portal already asked; record their answer directly
              // after commit. Don't bother the user in-app.
              stripePortalSurvey = {
                paymentMode: newSubscription.paymentMode,
                plan: newSubscription.plan,
                subscription_expiration: newSubscription.subscription_expiration,
                reason: derivationContext.stripeCancellationReason,
                free_text: derivationContext.stripeCancellationFeedback || null,
                source: 'stripe_portal',
                cancelled_at: Date.now(),
              };
            } else {
              // Set survey flag so the in-app modal fires on next load.
              writes["survey.cancel_reason_pending"] = true;
              writes["survey.cancel_detected_at"] = firestore.FieldValue.serverTimestamp();
              writes["survey.cancel_context"] = {
                paymentMode: newSubscription.paymentMode,
                plan: newSubscription.plan,
                subscription_expiration: newSubscription.subscription_expiration,
                cancelled_at: Date.now(),
              };
            }
          }

          tx.update(docRef, writes);
        });

        if (stripePortalSurvey) {
          await WRITE_CANCELLATION_SURVEY(stripePortalSurvey);
        }
      } catch (err) {
        console.error("updateSubscription transaction failed:", err);
      }
    };

    // Updates the cancellation-survey routing state on the user doc. All
    // transitions write to the same `survey.*` map (the snapshot listener
    // surfaces it as profile.survey). Params are independent and accumulate, so
    // a single call can e.g. mark answered AND record helped_pass:
    //  - preCancel: stamp pre_cancel_at so updateSubscription's post-cancel
    //    detection skips the in-app modal when the webhook arrives moments later.
    //  - helpedPass / reason: keystone signal + last reason, used by the
    //    post-cancel router to pick the success-story vs feedback ask.
    //  - answered: clear pending + stamp answered_at (user submitted a reason).
    //  - postCancelAction ('story'|'feedback'|'dismissed'): terminal — stops all
    //    further post-cancel asks for this cycle.
    //  - dismissed true/false: legacy persistent-link toggle in PremiumDetails.
    const setCancellationSurveyState = async ({
      answered,
      dismissed,
      preCancel,
      helpedPass,
      reason,
      postCancelAction,
      storySubmitted,
    }) => {
      if (isGhostMode()) return;
      try {
        const APP_NAME = IS_BUNDLE_APP
          ? BUNDLE_APP_NAME
          : await GET_APP_NAME_FOR_COLLECTION();
        const uid = auth().currentUser?.uid;
        if (!uid) return;
        const docRef = firestore()
          .collection("apps")
          .doc(APP_NAME)
          .collection("users")
          .doc(uid);

        const update = {};
        if (preCancel === true) {
          update["survey.pre_cancel_at"] = firestore.FieldValue.serverTimestamp();
          // Fresh attempt — clear any terminal action from a prior cycle so the
          // post-cancel router can route this attempt cleanly. Also clear the
          // story-submitted marker so a passer who resubscribes and cancels again
          // is re-offered the "share your story" link for the new cycle.
          update["survey.post_cancel_action"] = firestore.FieldValue.delete();
          update["survey.story_submitted"] = firestore.FieldValue.delete();
        }
        if (answered === true) {
          update["survey.cancel_reason_pending"] = false;
          update["survey.cancel_answered_at"] = firestore.FieldValue.serverTimestamp();
        }
        // helped_pass: true/false sets it; explicit null clears it (so a stale
        // "passed" from an abandoned attempt doesn't drive a later cancel's ask);
        // undefined leaves it untouched.
        if (helpedPass === null) {
          update["survey.helped_pass"] = firestore.FieldValue.delete();
        } else if (helpedPass !== undefined) {
          update["survey.helped_pass"] = helpedPass;
        }
        if (reason) {
          update["survey.last_reason"] = reason;
        }
        if (postCancelAction) {
          update["survey.post_cancel_action"] = postCancelAction;
        }
        // Set only on a real story publish (not skip/dismiss); gates the
        // persistent "share your story" link so it hides once actually shared.
        if (storySubmitted === true) {
          update["survey.story_submitted"] = true;
        }
        if (dismissed === true) {
          update["survey.cancel_prompt_dismissed"] = true;
        } else if (dismissed === false) {
          update["survey.cancel_prompt_dismissed"] = false;
        }

        if (Object.keys(update).length === 0) return;
        await docRef.update(update);
      } catch (err) {
        console.error("setCancellationSurveyState failed:", err);
      }
    };

    // Single source of truth for "is the user premium right now." Always derives
    // from raw RC events + Stripe Extension docs; the previous lazy gate that
    // skipped re-checks when subscription_expiration was still in the future is
    // what caused cancels to remain invisible until natural expiration. Safe to
    // call repeatedly: updateSubscription's transaction guards against stale
    // writes and the user-profile snapshot listener owns the access boolean
    // when no RC/Stripe signal is present (bulk_purchase / never-subscribed).
    async function getSubscriptionDetails() {
      try {
        const uid = isGhostMode() ? getGhostUid() : auth().currentUser?.uid;
        if (!uid) return false;

        const name = await GET_APP_NAME();
        const appName = IS_BUNDLE_APP ? BUNDLE_APP_NAME : name;

        const [metadata, events, stripeSubs, stripeOneTime] = await Promise.all([
          GET_REVENUECAT_METADATA(appName),
          GET_REVENUECAT_EVENTS(),
          GET_STRIPE_DETAILS(uid),
          GET_USER_STRIPE_ONE_TIME_PAYMENT(uid),
        ]);

        const allowedProductIds = metadata?.status && Array.isArray(metadata?.data)
          ? metadata.data.map((p) => p.id)
          : [];

        const rcState = deriveRevenueCatState(events?.data || [], allowedProductIds);
        const stripeState = deriveStripeState(stripeSubs?.data || []);
        const oneTimeState = deriveStripeOneTimeState(stripeOneTime?.data || []);

        const best = pickBestState([rcState, stripeState, oneTimeState]);
        if (!best) return false;

        let plan = best.plan ?? null;
        if (best.source === 'revenuecat' && best.product_id) {
          const planResult = await GET_REVENUECAT_SUBSCRIPTION_PLAN(best.product_id);
          if (planResult?.status) plan = planResult.data;
        }

        const derivedSubscription = {
          subscription_expiration: best.subscription_expiration,
          paymentMode: best.paymentMode,
          paymentType: best.paymentType,
          plan,
          will_renew: best.will_renew,
          status: best.status,
          derived_at: Date.now(), // informational: when we last derived
          derived_event_id: best.derived_event_id,
        };

        const isActive = isActiveAt(derivedSubscription.subscription_expiration);

        // Instrument the case where we couldn't determine renewal intent on an
        // active sub. If this fires >5% of actives, the RC event `type` field
        // probably isn't being persisted for some event kinds — fix at the
        // webhook layer.
        if (derivedSubscription.will_renew === null && isActive && posthog?.capture) {
          const daysToExpiration = Math.max(
            0,
            Math.ceil((derivedSubscription.subscription_expiration - Date.now()) / 86400000)
          );
          posthog.capture('subscription_will_renew_unknown', {
            paymentMode: derivedSubscription.paymentMode,
            days_to_expiration: daysToExpiration,
            source: best.source,
          });
        }

        // Pull Stripe Portal native cancellation reason from the winning state
        // (if Stripe was the source) so updateSubscription can dedup against
        // the in-app survey. These fields don't get persisted on the user doc.
        const derivationContext = {
          stripeCancellationReason: best.stripe_cancellation_reason || null,
          stripeCancellationFeedback: best.stripe_cancellation_feedback || null,
        };
        await updateSubscription(derivedSubscription, derivationContext);
        // NOTE: do NOT setSubscription(isActive) here. updateSubscription's
        // never-shorten / manual_override guards may preserve a longer
        // persisted (mobile-written) expiration than this derived candidate;
        // isSubscribe is driven by the persisted doc via the reactive effect,
        // so the header/paywalls can't diverge from the Profile screen.
        return isActive;
      } catch (err) {
        console.error('Subscription check failed:', err);
        return false;
      }
    }
  
    // Profile management functions
    function setProfile(data) {
      const newProfile = { ...data };
      setProfileState(newProfile);
      updateLocalStorage(newProfile);
  
      // Handle score progress - only save if today's score has changed
      const todayKey = moment().format("YYYY-MM-DD");
      const currentScore = newProfile?.readiness_scores?.readiness_score ?? 0;
      const existingScore = newProfile?.score_progress?.[todayKey];

      if (existingScore !== currentScore) {
        if (newProfile?.score_progress) {
          const updatedScoreProgress = {
            ...newProfile.score_progress,
            [todayKey]: currentScore
          };
          SAVE_SCORE_PROGRESS(updatedScoreProgress);
        } else {
          const item = {
            [todayKey]: currentScore,
          };
          SAVE_SCORE_PROGRESS(item);
        }
      }
    }
  
    function setQuestions(questions) {
      setQuestionScoreState({
        all_questions_scores: questions ?? [],
      });
    }
  
    function setArticles(articles) {
      setArticlesList(articles);
    }
  
    function setBundleProfile(data) {
      setBundleDataState(data);
    }
  
    async function setNewProfile(data) {
      const newProfile = { ...data };
      setProfileState(newProfile);
      updateLocalStorage(newProfile);
      return await UPDATE_USER_PROFILE(data);
    }
  
    function setNewBundleProfile(data) {
      setBundleDataState(data);
      UPDATE_BUNDLE_USER_PROFILE(data);
    }
  
    function setNotification(quiz, study) {
      const updatedProfile = {
        ...profile,
        quiz_of_the_day_reminder: quiz ?? true,
        study_plan_reminder: study ?? true,
      };
      setProfileState(updatedProfile);
      updateLocalStorage(updatedProfile);
      UPDATE_NOTIFICATION_SETTING(
        updatedProfile.quiz_of_the_day_reminder,
        updatedProfile.study_plan_reminder
      );
    }
  
    function setProfileDetails(firstName, lastName) {
      if (IS_BUNDLE_APP) {
        const updatedBundleData = {
          ...bundleData,
          auth_first_name: firstName ?? "",
          auth_last_name: lastName ?? "",
        };
        setBundleDataState(updatedBundleData);
        UPDATE_BUNDLE_PROFILE_DETAILS(firstName, lastName);
      } else {
        const updatedProfile = {
          ...profile,
          auth_first_name: firstName ?? "",
          auth_last_name: lastName ?? "",
        };
        setProfileState(updatedProfile);
        updateLocalStorage(updatedProfile);
        UPDATE_PROFILE_DETAILS(firstName, lastName);
      }
    }
  
    function setProfilePicture(url) {
      const updatedProfile = {
        ...profile,
        profile_pic: url ?? "",
      };
      setProfileState(updatedProfile);
      updateLocalStorage(updatedProfile);
    }
  
    function setBundleProfilePicture(url) {
      const updatedBundleData = {
        ...bundleData,
        profile_pic: url ?? "",
      };
      setBundleDataState(updatedBundleData);
    }
  
    function setTestOptions(time_per_question, allow_go_back) {
      const updatedProfile = {
        ...profile,
        time_per_question: time_per_question ?? 90,
        allow_go_back: allow_go_back ?? true,
      };
      setProfileState(updatedProfile);
      updateLocalStorage(updatedProfile);
      UPDATE_USER_TEST_OPTIONS(time_per_question, allow_go_back);
    }
  
    function setFontsOptions(font_size) {
      const updatedProfile = {
        ...profile,
        font_size: font_size ?? 1,
      };
      setProfileState(updatedProfile);
      updateLocalStorage(updatedProfile);
      UPDATE_USER_FONTS_OPTIONS(font_size);
    }
  
    async function setTestHistory(data) {
      console.log('🔍 [DEBUG] setTestHistory called');
      console.log('🔍 [DEBUG] setTestHistory input data:', data);
      console.log('🔍 [DEBUG] setTestHistory current profile:', profile);
      console.log('🔍 [DEBUG] setTestHistory profile.practice_test_history:', profile?.practice_test_history);
      
      const history = profile?.practice_test_history ? [...profile.practice_test_history] : [];
      console.log('🔍 [DEBUG] setTestHistory existing history length:', history.length);
      
      history.push(data);
      console.log('🔍 [DEBUG] setTestHistory updated history length:', history.length);
      console.log('🔍 [DEBUG] setTestHistory final history:', history);
      
      const updatedProfile = {
        ...profile,
        practice_test_history: history,
      };
      console.log('🔍 [DEBUG] setTestHistory updatedProfile:', updatedProfile);
      
      setProfileState(updatedProfile);
      updateLocalStorage(updatedProfile);
      
      // Use arrayUnion to add the single test data directly
      console.log('🔍 [DEBUG] setTestHistory using arrayUnion to add test data');
      try {
        const collection_user = await collection();
        await collection_user.update({
          practice_test_history: firestore.FieldValue.arrayUnion(data),
        });
        console.log('✅ [DEBUG] setTestHistory arrayUnion successful');
      } catch (error) {
        console.error('❌ [DEBUG] setTestHistory arrayUnion failed:', error);
      }
      console.log('✅ [DEBUG] setTestHistory completed');
    }
  
    function deleteTestHistory(data) {
      let history = _.cloneDeep(profile.practice_test_history);
      history = history.filter(item => JSON.stringify(item) != JSON.stringify(data));
      const updatedProfile = {
        ...profile,
        practice_test_history: history,
      };
      setProfileState(updatedProfile);
      updateLocalStorage(updatedProfile);
      DELETE_TEST_HISTORY(data);
    }
  
    function addFlashcardToBookmark(id) {
      let bookmarks = _.cloneDeep(profile.flashcard_bookmarks);
      if (bookmarks) {
        bookmarks.push(id);
      } else {
        bookmarks = [id];
      }
      const updatedProfile = {
        ...profile,
        flashcard_bookmarks: bookmarks,
      };
      setProfileState(updatedProfile);
      updateLocalStorage(updatedProfile);
      SAVE_FLASHCARD_BOOKMARK(bookmarks);
    }
  
    function removeFlashcardFromBookmark(id) {
      let bookmarks = _.cloneDeep(profile.flashcard_bookmarks);
      if (bookmarks) {
        bookmarks = bookmarks.filter(item => item != id);
      }
      const updatedProfile = {
        ...profile,
        flashcard_bookmarks: bookmarks,
      };
      setProfileState(updatedProfile);
      updateLocalStorage(updatedProfile);
      SAVE_FLASHCARD_BOOKMARK(bookmarks);
    }
  
    function addFlashcardProgress(setId, cardId) {
      const data = {
        flashcard_set_id: setId,
        last_seen_flashcard_id: cardId,
      };
      let progress = _.cloneDeep(profile.flashcards_progress);
      if (progress) {
        progress = progress.filter(item => item.flashcard_set_id != data.flashcard_set_id);
        progress.push(data);
      } else {
        progress = [data];
      }
      const updatedProfile = {
        ...profile,
        flashcards_progress: progress,
      };
      setProfileState(updatedProfile);
      updateLocalStorage(updatedProfile);
      SAVE_FLASHCARD_PROGRESS(progress);
    }
  
    function addQuestionToBookmark(id) {
      let bookmarks = _.cloneDeep(profile.question_bookmarks);
      if (bookmarks) {
        bookmarks.push(id);
      } else {
        bookmarks = [id];
      }
      const updatedProfile = {
        ...profile,
        question_bookmarks: bookmarks,
      };
      setProfileState(updatedProfile);
      updateLocalStorage(updatedProfile);
      SAVE_QUESTION_BOOKMARK(bookmarks);
    }
  
    function removeQuestionFromBookmark(id) {
      let bookmarks = _.cloneDeep(profile.question_bookmarks);
      if (bookmarks) {
        bookmarks = bookmarks.filter(item => item != id);
      }
      const updatedProfile = {
        ...profile,
        question_bookmarks: bookmarks,
      };
      setProfileState(updatedProfile);
      updateLocalStorage(updatedProfile);
      SAVE_QUESTION_BOOKMARK(bookmarks);
    }
  
    function addQuestionProgress(setId, questionId) {
      const data = {
        study_questions_set_id: setId,
        last_seen_question_id: questionId,
      };
      // Rebuild from the always-current profile, not the (possibly stale) closure.
      let progress = _.cloneDeep(profileRef.current?.questions_progress ?? []);
      progress = progress.filter(item => item.study_questions_set_id != data.study_questions_set_id);
      progress.push(data);
      const updatedProfile = {
        ...profileRef.current,
        questions_progress: progress,
      };
      // Keep the ref ahead of the lagging sync effect so rapid successive calls
      // build on the latest array rather than re-dropping each other's entries.
      profileRef.current = updatedProfile;
      setProfileState(updatedProfile);
      updateLocalStorage(updatedProfile);

      // Monotonic seq per set; only the latest enqueued write actually persists.
      const seq = (_latestSeenBySet.current.get(setId) ?? 0) + 1;
      _latestSeenBySet.current.set(setId, seq);

      _progressWriteChain.current = _progressWriteChain.current
        .then(() => {
          if (_latestSeenBySet.current.get(setId) !== seq) return; // superseded, skip
          // Persist the immutable array captured at call time. Re-reading
          // profileRef.current here would be unsafe: the realtime onSnapshot
          // listener can rewind it to the last server-acked value between
          // enqueue and execution, writing a stale cursor back to Firestore —
          // the exact 'frozen cursor' clobber this change targets.
          return SAVE_QUESTION_PROGRESS(progress);
        })
        .catch(() => {}); // never let one failure poison the chain for later writes
      return _progressWriteChain.current;
    }

    function addQuestionScore(question_id, topic_id, correct) {
      const domains = GET_APP_DOMAIN_LIST(selectedCertificate);
      
      let scores = questionScore?.all_questions_scores ?? [];
      const savedList = scores.filter(item => item.question_id == question_id);
      const saveItem = savedList.length > 0 ? savedList[0] : null;
      
      if (saveItem) {
        const score = correct ? saveItem.score + 1 : 0;
        const data = {
          question_id: question_id,
          topic_id: topic_id,
          times_answered: saveItem.times_answered + 1,
          last_seen: moment().format("YYYY MM DD HH:mm:ss"),
          score: score < 0 ? 0 : score,
          accuracy_score: score == 0 ? 0 : score == 1 ? 0.9 : 1,
        };
        SAVE_QUESTION(data);
        scores = scores.filter(item => item.question_id != question_id);
        scores.push(data);
      } else {
        const score = correct ? 1 : 0;
        const data = {
          question_id: question_id,
          topic_id: topic_id,
          times_answered: 1,
          last_seen: moment().format("YYYY MM DD HH:mm:ss"),
          score: score,
          accuracy_score: score == 1 ? 0.9 : 0,
        };
        SAVE_QUESTION(data);
        scores.push(data);
      }
  
      // Update question score state
      setQuestionScoreState({
        all_questions_scores: scores,
      });
  
      // Calculate and update readiness scores
      const readinessScores = calculateReadinessScores(scores, questionsList, domains);
      const updatedProfile = {
        ...profile,
        readiness_scores: readinessScores,
        score_progress: {
          ...profile.score_progress,
          [moment().format("YYYY-MM-DD")]: readinessScores.readiness_score,
        }
      };
      setProfileState(updatedProfile);
      updateLocalStorage(updatedProfile);
      
      SAVE_SCORE_PROGRESS(updatedProfile.score_progress);
      SAVE_PROGRESS_DASHBOARD(readinessScores);
  
      // Update community data
      const communityData = {
        question_id: question_id,
        topic_id: topic_id,
        correct: correct,
        questionsList: questionsList,
        app_domains: domains,
        scores: scores,
      };
      UPDATE_COMMUNITY_DATA(communityData);
    }
  
    function addAllQuestionScore(history) {
      const domains = GET_APP_DOMAIN_LIST(selectedCertificate);
      
      let scores = questionScore?.all_questions_scores ?? [];
      
      for (let index in history) {
        const obj = history[index];
        const savedList = scores.filter(item => item.question_id == obj.question_id);
        const saveItem = savedList.length > 0 ? savedList[0] : null;
        
        if (saveItem) {
          const score = obj.correct ? saveItem.score + 1 : 0;
          const data = {
            question_id: obj.question_id,
            topic_id: obj.topic_id,
            times_answered: saveItem.times_answered + 1,
            last_seen: moment().format("YYYY MM DD HH:mm:ss"),
            score: score < 0 ? 0 : score,
            accuracy_score: score == 0 ? 0 : score == 1 ? 0.9 : 1,
          };
          SAVE_QUESTION(data);
          scores = scores.filter(item => item.question_id != obj.question_id);
          scores.push(data);
        } else {
          const score = obj.correct ? 1 : 0;
          const data = {
            question_id: obj.question_id,
            topic_id: obj.topic_id,
            times_answered: 1,
            last_seen: moment().format("YYYY MM DD HH:mm:ss"),
            score: score,
            accuracy_score: score == 1 ? 0.9 : 0,
          };
          SAVE_QUESTION(data);
          scores.push(data);
        }
      }
  
      // Update question score state
      setQuestionScoreState({
        all_questions_scores: scores,
      });
  
      // Calculate and update readiness scores
      const readinessScores = calculateReadinessScores(scores, questionsList, domains);
      
      // Handle case where profile might be null (for new users)
      const currentProfile = profile || {};
      const currentScoreProgress = currentProfile.score_progress || {};
      
      const updatedProfile = {
        ...currentProfile,
        readiness_scores: readinessScores,
        score_progress: {
          ...currentScoreProgress,
          [moment().format("YYYY-MM-DD")]: readinessScores.readiness_score,
        }
      };
      setProfileState(updatedProfile);
      updateLocalStorage(updatedProfile);
      
      SAVE_SCORE_PROGRESS(updatedProfile.score_progress);
      SAVE_PROGRESS_DASHBOARD(readinessScores);
  
      // Update community data for all history
      for (let index in history) {
        const communityData = history[index];
        if (communityData.selected != STRING.unanswered_tag) {
          UPDATE_COMMUNITY_DATA(communityData);
        }
      }
    }
  
    function updateReadinessScore(data) {
      const readinessScores = calculateReadinessScores(data.scores, data.questionsList, data.app_domains);
      const updatedProfile = {
        ...profile,
        readiness_scores: readinessScores,
      };
      setProfileState(updatedProfile);
      updateLocalStorage(updatedProfile);
    }

    // Explicit "Recalculate Readiness Scores" maintenance action (Profile screen). Mirrors
    // mobile: migrate each stored score's topic_id to the current bundle, drop orphan records
    // (questions retired from the bundle), recompute readiness, and persist. The pure diff +
    // recompute is the shared core (planReadinessRecalculation); the Firebase writes stay here.
    async function recalculateReadinessScores() {
      try {
        // Re-check auth before any destructive write — getEffectiveUid() resolves at write
        // time, so a sign-out/account-switch mid-flow must not target a stale uid (mobile parity).
        if (!auth().currentUser) {
          return { status: false, error: "Please sign in again to recalculate your scores." };
        }

        const scoresResult = await GET_SCORE_QUESTIONS();
        if (!scoresResult?.status) {
          return { status: false, error: scoresResult?.error || "Failed to load question scores" };
        }
        const userScores = scoresResult.data || [];
        if (userScores.length === 0) {
          return { status: true, error: "", message: "No scores to recalculate" };
        }

        // Guard against an unloaded/stale bundle — questionsList is [] until questions load
        // (and is reset on every exam switch). Recalculating against an empty bundle would
        // flag every score as an orphan (mobile has the same guard).
        if (!questionsList || questionsList.length === 0) {
          return { status: false, error: "Questions are still loading. Please try again in a moment." };
        }

        const domains = GET_APP_DOMAIN_LIST(selectedCertificate);
        const plan = planReadinessRecalculation(userScores, questionsList, domains);

        // Defense-in-depth: the core refuses to produce deletions in these cases, and we never
        // persist a recompute built on a bundle we don't trust. Distinct copy per cause —
        // bundleEmpty is transient (questions not loaded), allOrphaned is a real data state.
        if (plan.bundleEmpty) {
          return { status: false, error: "Questions are still loading. Please try again in a moment." };
        }
        if (plan.allOrphaned) {
          return { status: false, error: "No matching questions found to recalculate." };
        }

        // Apply the migration atomically; surface a real write failure instead of claiming success.
        if (plan.hasChanges) {
          const writeResult = await MIGRATE_QUESTION_SCORES(plan.scoresToSave, plan.deletedQuestionIds);
          if (!writeResult?.status) {
            return { status: false, error: writeResult?.error || "Failed to save recalculated scores. Please try again." };
          }
        }
        const dashboardResult = await SAVE_PROGRESS_DASHBOARD(plan.readinessScores);
        if (dashboardResult && dashboardResult.status === false) {
          return { status: false, error: dashboardResult.error || "Failed to save readiness scores. Please try again." };
        }

        // Reflect the reconciled data in memory so the dashboard updates immediately.
        setAllQuestionData(plan.migratedScores);
        const updatedProfile = {
          ...(profile || {}),
          readiness_scores: plan.readinessScores,
        };
        setProfileState(updatedProfile);
        updateLocalStorage(updatedProfile);

        const message = plan.hasChanges
          ? `Scores recalculated — ${plan.updatedCount} updated, ${plan.deletedCount} removed.`
          : "Scores recalculated successfully";
        return { status: true, error: "", message };
      } catch (error) {
        console.error("[recalculateReadinessScores] Error:", error);
        return { status: false, error: error.message || "Failed to recalculate readiness scores" };
      }
    }
  
    function setCommunityData(data) {
      setCommunityState({
        questions_version: data.questions_version,
        total_time_per_question: data.total_time_per_question,
        android_app_verions: data?.android_app_verions,
        android_build_number: data?.android_build_number,
        ios_app_version: data?.ios_app_version,
        ios_build_number: data?.ios_build_number,
        is_forcefully_new_version: data?.is_forcefully_new_version,
      });
    }
  
    function setAllQuestionData(data) {
      setQuestionScoreState({
        all_questions_scores: data,
      });
    }
  
    function setExamDate(date) {
      const updatedProfile = {
        ...profile,
        exam_date: date,
      };
      setProfileState(updatedProfile);
      updateLocalStorage(updatedProfile);
    }
  
    // Article-related functions
    async function addArticleToBookmark(id) {
      console.log("addArticleToBookmark", id);
      let bookmarks = _.cloneDeep(articles.bookmarks || []);
      if (bookmarks) {
        bookmarks.push(id);
      } else {
        bookmarks = [id];
      }
      const updatedArticles = {
        ...articles,
        bookmarks: bookmarks,
      };
      setArticlesState(updatedArticles);
      updateArticlesLocalStorage(updatedArticles);
      
      // Save to Firebase
      try {
        await SAVE_ARTICLE_BOOKMARK(bookmarks);
      } catch (error) {
        console.error("Error saving article bookmark:", error);
      }
    }
  
    async function removeArticleFromBookmark(id) {
      console.log("removeArticleFromBookmark", id);
      let bookmarks = _.cloneDeep(articles.bookmarks || []);
      if (bookmarks) {
        bookmarks = bookmarks.filter(item => item != id);
      }
      const updatedArticles = {
        ...articles,
        bookmarks: bookmarks,
      };
      setArticlesState(updatedArticles);
      updateArticlesLocalStorage(updatedArticles);
      
      // Save to Firebase
      try {
        await SAVE_ARTICLE_BOOKMARK(bookmarks);
      } catch (error) {
        console.error("Error saving article bookmark:", error);
      }
    }
  
    function addArticleProgress(setId, articleId) {
      const data = {
        articles_set_id: String(setId),
        last_seen_article_id: articleId,
      };
      let progress = _.cloneDeep(profile.articles_progress);
      if (progress) {
        progress = progress.filter(item => String(item.articles_set_id) !== data.articles_set_id);
        progress.push(data);
      } else {
        progress = [data];
      }
      const updatedProfile = {
        ...profile,
        articles_progress: progress,
      };
      setProfileState(updatedProfile);
      updateLocalStorage(updatedProfile);
      SAVE_ARTICLE_PROGRESS(progress);
    }

    async function markArticleAsRead(articleId, articleData) {
      try {
        // Pass current summary from state to avoid reading from Firestore (optimization)
        const result = await MARK_ARTICLE_AS_READ(articleId, articleData, articles.readSummary);
        if (result.status) {
          // Update local articles state with new summary data
          const updatedArticles = {
            ...articles,
            readSummary: result.data.summary,
            // Note: Individual article data is handled by Firebase listener
          };
          setArticlesState(updatedArticles);
          updateArticlesLocalStorage(updatedArticles);
        }
        return result;
      } catch (error) {
        console.error("Error marking article as read:", error);
        return { status: false, error: error.message };
      }
    }

    async function getArticleReadStatus(articleId) {
      try {
        const result = await GET_ARTICLE_READ_STATUS(articleId);
        return result;
      } catch (error) {
        console.error("Error getting article read status:", error);
        return { status: false, error: error.message };
      }
    }

    async function getArticleReadSummary() {
      try {
        const result = await GET_ARTICLE_READ_SUMMARY();
        if (result.status) {
          // Update local articles state with summary data
          const updatedArticles = {
            ...articles,
            readSummary: result.data
          };
          setArticlesState(updatedArticles);
          updateArticlesLocalStorage(updatedArticles);
        }
        return result;
      } catch (error) {
        console.error("Error getting article read summary:", error);
        return { status: false, error: error.message };
      }
    }

    async function getReadArticlesByTopic(topicId) {
      try {
        const result = await GET_READ_ARTICLES_BY_TOPIC(topicId);
        return result;
      } catch (error) {
        console.error("Error getting read articles by topic:", error);
        return { status: false, error: error.message };
      }
    }

    async function getArticleBookmarks() {
      try {
        const result = await GET_ARTICLE_BOOKMARKS();
        if (result.status) {
          // Update local articles state with bookmarks data
          const updatedArticles = {
            ...articles,
            bookmarks: result.data
          };
          setArticlesState(updatedArticles);
          updateArticlesLocalStorage(updatedArticles);
        }
        return result;
      } catch (error) {
        console.error("Error getting article bookmarks:", error);
        return { status: false, error: error.message };
      }
    }

    // Article reaction functions
    async function likeArticle(articleId, topicId) {
      try {
        const result = await SAVE_ARTICLE_REACTION(articleId, 'like', { topic_id: topicId });
        if (result.status) {
          // Update local state with new reaction summary will be handled by Firebase listener
          console.log('Article liked:', articleId);
        }
        return result;
      } catch (error) {
        console.error("Error liking article:", error);
        return { status: false, error: error.message };
      }
    }

    async function dislikeArticle(articleId, topicId) {
      try {
        const result = await SAVE_ARTICLE_REACTION(articleId, 'dislike', { topic_id: topicId });
        if (result.status) {
          console.log('Article disliked:', articleId);
        }
        return result;
      } catch (error) {
        console.error("Error disliking article:", error);
        return { status: false, error: error.message };
      }
    }

    async function removeArticleReaction(articleId, topicId) {
      try {
        const result = await SAVE_ARTICLE_REACTION(articleId, null, { topic_id: topicId });
        if (result.status) {
          console.log('Article reaction removed:', articleId);
        }
        return result;
      } catch (error) {
        console.error("Error removing article reaction:", error);
        return { status: false, error: error.message };
      }
    }

    async function getArticleReactionSummary() {
      try {
        const result = await GET_ARTICLE_REACTION_SUMMARY();
        if (result.status) {
          // Update local articles state with reaction summary data
          const updatedArticles = {
            ...articles,
            reactionSummary: result.data
          };
          setArticlesState(updatedArticles);
          updateArticlesLocalStorage(updatedArticles);
        }
        return result;
      } catch (error) {
        console.error("Error getting article reaction summary:", error);
        return { status: false, error: error.message };
      }
    }

    // QOTD functions
    async function submitQOTDResponse(qotdResult) {
      try {
        const result = await UPDATE_USER_QOTD(qotdResult);
        return result;
      } catch (error) {
        console.error("Error submitting QOTD response:", error);
        return { status: false, error: error.message };
      }
    }

    async function getQOTDSummary() {
      try {
        const result = await GET_USER_QOTD_SUMMARY();
        return result;
      } catch (error) {
        console.error("Error getting QOTD summary:", error);
        return { status: false, error: error.message };
      }
    }

    async function getQOTDResponse(date) {
      try {
        const result = await GET_USER_QOTD_RESPONSE(date);
        return result;
      } catch (error) {
        console.error("Error getting QOTD response:", error);
        return { status: false, error: error.message };
      }
    }

    async function getQOTDResponses(startDate, endDate) {
      try {
        const result = await GET_USER_QOTD_RESPONSES(startDate, endDate);
        return result;
      } catch (error) {
        console.error("Error getting QOTD responses:", error);
        return { status: false, error: error.message };
      }
    }
  
    // Recompute readiness from live scores for DISPLAY so the dashboard reflects the corrected,
    // bundle-anchored counts immediately (matching the custom-test builder) without waiting for the
    // persisted readiness_scores to be rewritten on the next answer/recalc. Display-only — no writes.
    const profileForDisplay = React.useMemo(() => {
      const scores = questionScore?.all_questions_scores ?? [];
      if (scores.length === 0 || !questionsList || questionsList.length === 0) {
        // Not enough live data yet — keep the persisted value, avoid flicker to zeros.
        return profile;
      }
      const domains = GET_APP_DOMAIN_LIST(selectedCertificate);
      if (!domains || domains.length === 0) return profile;
      const readiness = calculateReadinessScoresCore(scores, questionsList, domains);
      return { ...profile, readiness_scores: readiness };
    }, [profile, questionScore, questionsList, selectedCertificate]);

    return (
      <AuthContext.Provider
        value={{
          user: auth().currentUser,
          isAuthenticated: auth().currentUser !== null,
          isAuthLoading,
          profile: profileForDisplay,
          articles,
          qotd,
          bundleData,
          community,
          questionsList,
          articlesList,
          loadArticlesList,
          flashCardsList,
          acronymsList,
          glossaryList,
          isSubscribe,
          isVisibleCommunity,
          questionScore,
          setProfile,
          setBundleProfile,
          setNewProfile,
          setNewBundleProfile,
          setNotification,
          setProfileDetails,
          setProfilePicture,
          setBundleProfilePicture,
          setTestOptions,
          setSubscription,
          setTestHistory,
          deleteTestHistory,
          addFlashcardToBookmark,
          removeFlashcardFromBookmark,
          addFlashcardProgress,
          addQuestionToBookmark,
          removeQuestionFromBookmark,
          addQuestionProgress,
          addQuestionScore,
          addArticleToBookmark,
          removeArticleFromBookmark,
          addArticleProgress,
          markArticleAsRead,
          getArticleReadStatus,
          getArticleReadSummary,
          getArticleBookmarks,
          getReadArticlesByTopic,
          likeArticle,
          dislikeArticle,
          removeArticleReaction,
          getArticleReactionSummary,
          submitQOTDResponse,
          getQOTDSummary,
          getQOTDResponse,
          getQOTDResponses,
          setCommunityData,
          setQuestionsList,
          setArticles,
          addAllQuestionScore,
          getSubscriptionDetails,
          setCancellationSurveyState,
          setVisibleCommunity,
          setFontsOptions,
          setAllQuestionData,
          updateReadinessScore,
          recalculateReadinessScores,
          setExamDate
        }}
      >
        {props.children}
      </AuthContext.Provider>
    );
  };